import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import FileUpload from "../components/FileUpload";

const TOUR_LEVELS = [
  "Club Amateur",
  "Elite Amateur — Junior",
  "Elite Amateur — Senior",
  "Collegiate / University Player",
  "New Professional",
  "Mini Tour Player",
  "Development Tour Player",
  "Main Tour Professional",
  "Other / Prefer not to say",
];

const initialState = {
  name: "", email: "", country: "Ireland", age: 18, handicap: "",
  golf_club: "", ranking: "", tour_level: "",
  story: "", funding_goal: 15000, image_url: "",
  social_instagram: "", social_twitter: "", references: "",
};

export default function Apply() {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const handicapVal = form.handicap === "" || form.handicap === null ? null : Number(form.handicap);
      await api.post("/applications", {
        ...form,
        age: Number(form.age),
        handicap: Number.isFinite(handicapVal) ? handicapVal : null,
        tour_level: form.tour_level || null,
        funding_goal: Number(form.funding_goal),
        social_links: {
          instagram: form.social_instagram || null,
          twitter: form.social_twitter || null,
        },
      });
      setSubmitted(true);
      toast.success("Application submitted, we'll be in touch within 5 working days.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="container-tight py-32 text-center" data-testid="apply-success">
        <div className="overline">Application received</div>
        <h1 className="font-display text-5xl md:text-6xl mt-3">Thank you.</h1>
        <p className="mt-6 max-w-xl mx-auto text-foreground/70">
          Our review team will assess your application and respond within five
          working days. In the meantime, follow @talkgolfinternational for updates.
        </p>
      </div>
    );
  }

  return (
    <div className="py-16 md:py-24" data-testid="apply-page">
      <div className="container-tight max-w-3xl">
        <div className="overline">Get listed for sponsorship</div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-3 tracking-tight leading-[1.02]">
          Tell us <span className="text-primary">your story</span>.
        </h1>
        <p className="mt-6 text-foreground/70">
          Apply to be listed on the TGI Golfer Fund so supporters and sponsors can back you directly. We verify every
          golfer, club, level and references, before activating your public profile.
        </p>

        <form onSubmit={submit} className="mt-12 space-y-10" data-testid="apply-form">
          <Section title="About you">
            <Field label="Full name" required>
              <input required value={form.name} onChange={set("name")} className="input" data-testid="apply-name" />
            </Field>
            <Field label="Email" required>
              <input required type="email" value={form.email} onChange={set("email")} className="input" data-testid="apply-email" />
            </Field>
            <Field label="Country" required>
              <input required value={form.country} onChange={set("country")} className="input" data-testid="apply-country" />
            </Field>
            <Field label="Age" required>
              <input required type="number" min="14" max="40" value={form.age} onChange={set("age")} className="input" data-testid="apply-age" />
            </Field>
            <Field label="Profile image" full>
              <FileUpload
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                label=""
                testid="apply-image-upload"
                aspect="aspect-[4/5] md:aspect-[3/2]"
              />
            </Field>
          </Section>

          <Section title="Playing record">
            <Field label="Handicap / playing level (optional)">
              <input
                type="number"
                step="0.1"
                value={form.handicap}
                onChange={set("handicap")}
                placeholder="e.g. 2.4 (leave blank if you don't have one yet)"
                className="input"
                data-testid="apply-handicap"
              />
            </Field>
            <Field label="Current ranking (optional)">
              <input value={form.ranking} onChange={set("ranking")} placeholder="e.g. WAGR #287" className="input" data-testid="apply-ranking" />
            </Field>
            <Field label="Tour / level (optional)">
              <select value={form.tour_level} onChange={set("tour_level")} className="input" data-testid="apply-tour">
                <option value="">Select a level…</option>
                {TOUR_LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </Field>
            <Field label="Golf club / business">
              <input value={form.golf_club} onChange={set("golf_club")} placeholder="business name" className="input" data-testid="apply-club-business" />
            </Field>
          </Section>

          <Section title="Your journey">
            <Field label="Tell us your story" required full>
              <textarea
                required
                rows={6}
                value={form.story}
                onChange={set("story")}
                className="input"
                placeholder="Where you've come from, where you're headed, and why this matters."
                data-testid="apply-story"
              />
            </Field>
            <Field label="Funding goal (EUR)" required>
              <input required type="number" min="1000" value={form.funding_goal} onChange={set("funding_goal")} className="input" data-testid="apply-goal" />
            </Field>
            <Field label="References (coaches, club, etc.)" full>
              <textarea rows={3} value={form.references} onChange={set("references")} className="input" data-testid="apply-references" />
            </Field>
            <Field label="Instagram">
              <input value={form.social_instagram} onChange={set("social_instagram")} placeholder="https://instagram.com/…" className="input" data-testid="apply-instagram" />
            </Field>
            <Field label="Twitter / X">
              <input value={form.social_twitter} onChange={set("social_twitter")} placeholder="https://twitter.com/…" className="input" data-testid="apply-twitter" />
            </Field>
          </Section>

          <div className="border-t border-border pt-8">
            <button disabled={loading} className="btn-primary w-full md:w-auto md:px-12" data-testid="apply-submit">
              {loading ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </form>
      </div>

      <style>{`.input { width: 100%; height: 3rem; padding: 0 1rem; background: transparent; border: 1px solid hsl(var(--border)); outline: none; font: inherit; } .input:focus { border-color: hsl(var(--primary)); } textarea.input { height: auto; padding: 0.75rem 1rem; }`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="overline mb-6">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function Field({ label, children, full, required }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-foreground/60 mb-2 block">{label}{required && <span className="text-primary"> *</span>}</span>
      {children}
    </label>
  );
}
